var RATIO_16_9 = 16 / 9;
var RATIO_4_3 = 4 / 3;
/** Streams closer to 16:9 than 4:3 fill the panel; squarer streams keep original proportions. */
var WIDESCREEN_RATIO_THRESHOLD = (RATIO_16_9 + RATIO_4_3) / 2;

export var AVPLAY_DISPLAY_FULL = 'PLAYER_DISPLAY_MODE_FULL_SCREEN';
export var AVPLAY_DISPLAY_LETTERBOX = 'PLAYER_DISPLAY_MODE_LETTER_BOX';

/**
 * @param {number} width
 * @param {number} height
 * @returns {number|null}
 */
export function getStreamAspectRatio(width, height) {
  if (!width || !height || width <= 0 || height <= 0) {
    return null;
  }
  return width / height;
}

/**
 * True when the stream is closer to 16:9 than 4:3.
 * @param {number} width
 * @param {number} height
 * @returns {boolean}
 */
export function isWidescreenAspect(width, height) {
  var ratio = getStreamAspectRatio(width, height);
  if (ratio == null) {
    return true;
  }
  return ratio >= WIDESCREEN_RATIO_THRESHOLD;
}

/**
 * HTML5 object-fit for any stream (HTTP live, UDP multicast, etc.).
 * @param {number} width
 * @param {number} height
 * @returns {'cover'|'contain'}
 */
export function getVideoObjectFit(width, height) {
  return isWidescreenAspect(width, height) ? 'cover' : 'contain';
}

/**
 * Samsung avplay / epavplay display mode for any stream.
 * @param {number} width
 * @param {number} height
 * @returns {string}
 */
export function getAvplayDisplayMethod(width, height) {
  return isWidescreenAspect(width, height) ? AVPLAY_DISPLAY_FULL : AVPLAY_DISPLAY_LETTERBOX;
}

/**
 * @param {string} eventType
 * @param {*} eventData
 * @returns {{ width: number, height: number }|null}
 */
export function parseTizenVideoResolution(eventType, eventData) {
  if (eventType !== 'PLAYER_MSG_RESOLUTION_CHANGED' || eventData == null) {
    return null;
  }

  if (typeof eventData === 'object') {
    var objectWidth = eventData.width || eventData.videoWidth;
    var objectHeight = eventData.height || eventData.videoHeight;
    if (objectWidth > 0 && objectHeight > 0) {
      return { width: objectWidth, height: objectHeight };
    }
  }

  if (typeof eventData === 'string') {
    try {
      var parsed = JSON.parse(eventData);
      if (parsed.width > 0 && parsed.height > 0) {
        return { width: parsed.width, height: parsed.height };
      }
    } catch (e) {
      var match = eventData.match(/(\d+)\s*[xX,]\s*(\d+)/);
      if (match) {
        return {
          width: parseInt(match[1], 10),
          height: parseInt(match[2], 10),
        };
      }
    }
  }

  return null;
}

/**
 * Read video width/height from avplay stream track metadata.
 * @param {*} avplay
 * @returns {{ width: number, height: number }|null}
 */
export function getVideoDimensionsFromAvplay(avplay) {
  if (!avplay) {
    return null;
  }

  var getters = ['getCurrentStreamInfo', 'getTotalTrackInfo'];
  for (var g = 0; g < getters.length; g++) {
    var getterName = getters[g];
    if (typeof avplay[getterName] !== 'function') {
      continue;
    }

    try {
      var tracks = avplay[getterName]();
      if (!tracks) {
        continue;
      }
      if (!Array.isArray(tracks)) {
        tracks = [tracks];
      }

      for (var i = 0; i < tracks.length; i++) {
        var track = tracks[i];
        if (!track) {
          continue;
        }

        var type = String(track.type || track.trackType || '').toUpperCase();
        if (type && type !== 'VIDEO') {
          continue;
        }

        var extra = track.extra_info || track.extraInfo;
        if (typeof extra === 'string') {
          try {
            var info = JSON.parse(extra);
            if (info.width > 0 && info.height > 0) {
              return { width: info.width, height: info.height };
            }
          } catch (parseErr) {
            // ignore malformed extra_info
          }
        }

        if (track.width > 0 && track.height > 0) {
          return { width: track.width, height: track.height };
        }
      }
    } catch (e) {
      // try next getter
    }
  }

  return null;
}

/**
 * Apply Samsung display mode from stream dimensions (HTTP, UDP live, etc.).
 * @param {*} avplay
 * @param {{ width: number, height: number }|null} dimensions
 * @returns {boolean}
 */
export function applyAvplayDisplayMode(avplay, dimensions) {
  if (!avplay || typeof avplay.setDisplayMethod !== 'function') {
    return false;
  }

  var dims = dimensions;
  if (!dims) {
    dims = getVideoDimensionsFromAvplay(avplay);
  }
  if (!dims) {
    return false;
  }

  try {
    var mode = getAvplayDisplayMethod(dims.width, dims.height);
    avplay.setDisplayMethod(mode);
    console.log('CHANNELS: stream aspect display', mode, dims.width + 'x' + dims.height);
    return true;
  } catch (e) {
    console.warn('CHANNELS: setDisplayMethod failed:', e.message || e);
    return false;
  }
}

/**
 * @param {HTMLVideoElement} video
 * @returns {'cover'|'contain'}
 */
export function getVideoObjectFitForElement(video) {
  if (!video) {
    return 'cover';
  }
  if (video.videoWidth > 0 && video.videoHeight > 0) {
    return getVideoObjectFit(video.videoWidth, video.videoHeight);
  }
  return 'cover';
}
